import { Suspense, useState } from "react";
import {
  Collapse,
  Fab,
  IconButton,
  styled,
  Typography,
  Zoom,
} from "@mui/material";
import { Close, FormatListBulleted } from "@mui/icons-material";
import { useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { ControlOptions } from "leaflet";
import { ErrorBoundary } from "react-error-boundary";

import { PaddedPaper } from "components/surfaces/PaddedPaper";
import { LegendItemSkeleton } from "components/skeletons/LegendItemSkeleton";
import { MapControlBase } from "components/map/utils/MapControlBase";
import { LegendAccordion } from "../LegendAccordion";
import { useSelectedPersonalLayers } from "hooks/layerHooks";
import { isWMSLayer, useSelectedBackgroundLayer } from "hooks/mapHooks";

interface Props extends ControlOptions {}

export function LegendControl(props: Props) {
  const { ...controlProps } = props;

  const selectedPersonalLayers = useSelectedPersonalLayers();
  const selectedBackgroundLayer = useSelectedBackgroundLayer();

  const { t } = useTranslation();
  const map = useMap();

  const [showLegend, setShowLegend] = useState<boolean>(true);

  const mapSize = map.getSize();

  function onToggleLegend() {
    setShowLegend((prev) => !prev);
  }

  return (
    <MapControlBase {...controlProps}>
      <Styles mapWidth={mapSize.x}>
        <Zoom in={!showLegend}>
          <Fab
            size="small"
            variant="circular"
            onClick={onToggleLegend}
            className={showLegend ? "closed" : ""}
          >
            <FormatListBulleted />
          </Fab>
        </Zoom>
        <Collapse in={showLegend} className="panel-wrapper" unmountOnExit>
          <PaddedPaper className="container">
            <div className="header">
              <Typography variant="panel-title">{t("label.legend")}</Typography>
              <IconButton onClick={onToggleLegend} edge="end" size="small">
                <Close fontSize="small" />
              </IconButton>
            </div>
            <div className="accordion-container">
              {selectedPersonalLayers.length === 0 &&
                (!isWMSLayer(selectedBackgroundLayer) ||
                  !selectedBackgroundLayer.legend) && (
                  <Typography>{t("label.noActiveLayerWithLegend")}</Typography>
                )}
              <ErrorBoundary fallbackRender={() => null}>
                {isWMSLayer(selectedBackgroundLayer) &&
                  selectedBackgroundLayer.legend && (
                    <Suspense fallback={<LegendItemSkeleton />}>
                      <LegendAccordion
                        id={selectedBackgroundLayer.id}
                        wms={selectedBackgroundLayer.wms}
                        label={selectedBackgroundLayer.name}
                        attribution={selectedBackgroundLayer.attribution}
                      />
                    </Suspense>
                  )}
                <Suspense fallback={<LegendItemSkeleton />}>
                  {selectedPersonalLayers.length > 0 &&
                    selectedPersonalLayers.map((layer) => (
                      <LegendAccordion
                        id={layer.id}
                        wms={layer.wms}
                        label={layer.name}
                        key={layer.id}
                      />
                    ))}
                </Suspense>
              </ErrorBoundary>
            </div>
          </PaddedPaper>
        </Collapse>
      </Styles>
    </MapControlBase>
  );
}

interface StyleProps {
  mapWidth: number;
}

const Styles = styled("div", {
  shouldForwardProp: (prop) => ["mapWidth"].includes(prop.toString()) === false,
})<StyleProps>`
  & > * {
    float: right;
  }
  .panel-wrapper {
    width: 100%;
    max-width: 300px;
  }
  .container {
    background-color: ${({ theme }) => theme.palette.common.white};
  }
  .accordion-container {
    max-height: 35vh;
    overflow-y: auto;
    margin-right: ${({ theme }) => theme.spacing(-2)};
    padding-right: ${({ theme }) => theme.spacing(2)};
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .closed {
    display: none;
  }
`;
